// /calendar: upcoming days with key, kit, instruments, capacity and "Write for this day".
import { useEffect, useState } from "react";
import { getPreset, KITS, keyPairName, type PeriodDef, getAtmosKit } from "@eb/shared";
import { api } from "../api";
import { Link, useTitle } from "../lib/router";
import { formatPlayTime } from "../river/slots";

interface CalDay {
  day: number;
  date: string;
  period: PeriodDef;
  capacity: { total: number; approved: number; reserved: number };
  writable: boolean;
  frozen: boolean;
  freezeAt: number;
}

export function Calendar() {
  useTitle("Calendar");
  const [days, setDays] = useState<CalDay[] | null>(null);
  const [today, setToday] = useState(0);
  const [error, setError] = useState("");
  useEffect(() => {
    api<{ today: number; days: CalDay[] }>("GET", "/api/calendar")
      .then((r) => {
        setDays(r.days);
        setToday(r.today);
      })
      .catch((e) => setError(String(e)));
  }, []);
  return (
    <main id="main" className="page">
      <h1>Calendar</h1>
      <p className="muted">
        Every day is one period of the song with its own key, drum kit and a mixed set of instruments. You can write for
        days 1–7 days ahead; a day's lineup freezes an hour before it starts (UTC).
      </p>
      {error && <p role="alert">{error}</p>}
      {!days && !error && <p className="muted">Loading…</p>}
      <ol className="calendar" aria-label="Upcoming days">
        {days?.map((d) => {
          const used = d.capacity.approved + d.capacity.reserved;
          const pct = Math.min(100, (used / d.capacity.total) * 100);
          const label =
            d.day === today
              ? "Today"
              : d.day === today + 1
                ? "Tomorrow"
                : new Date(`${d.date}T12:00:00Z`).toLocaleDateString(undefined, { weekday: "long" });
          return (
            <li key={d.day} className="cal-day panel" data-testid={`cal-${d.day}`}>
              <div className="cal-date">
                <strong>{label}</strong>
                <span className="muted">
                  {new Date(`${d.date}T12:00:00Z`).toLocaleDateString(undefined, {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              </div>
              <div className="cal-body">
                {d.period.genre && <p className="cal-genre muted">{d.period.genre}</p>}
                <dl className="cal-facts">
                  <div>
                    <dt>Key:</dt>
                    <dd className="cal-key">
                      <strong>{keyPairName(d.period.key)}</strong>
                    </dd>
                  </div>
                  <div>
                    <dt>Drum kit:</dt>
                    <dd>
                      {KITS[d.period.kit].name} <span className="muted">({KITS[d.period.kit].genre})</span>
                    </dd>
                  </div>
                  <div>
                    <dt>Instruments:</dt>
                    <dd data-testid="cal-instruments">
                      {getPreset(d.period.instruments.bass).name} · {getPreset(d.period.instruments.harmony).name} ·{" "}
                      {getPreset(d.period.instruments.lead).name} · {getPreset(d.period.instruments.classic).name}
                    </dd>
                  </div>
                  <div>
                    <dt>Atmosphere:</dt>
                    <dd data-testid="cal-atmos">{getAtmosKit(d.period.atmosKit).name}</dd>
                  </div>
                </dl>
                <div
                  className="capacity"
                  role="meter"
                  aria-valuemin={0}
                  aria-valuemax={d.capacity.total}
                  aria-valuenow={used}
                  aria-label="Slots taken"
                >
                  <span style={{ width: `${pct}%` }} />
                </div>
                <p className="small muted">
                  {d.capacity.approved} approved · {d.capacity.reserved} being written · {d.capacity.total - used} free
                  of {d.capacity.total}
                </p>
              </div>
              <div className="cal-action">
                {d.writable ? (
                  <Link className="btn btn-primary" href={`/write?day=${d.day}`} data-testid={`write-${d.day}`}>
                    Write for this day
                  </Link>
                ) : d.frozen ? (
                  <Link className="btn" href={`/day/${d.date}`}>
                    {d.day < today ? "Replay" : "See lineup"}
                  </Link>
                ) : (
                  <span className="muted small">
                    {d.day <= today
                      ? "Playing"
                      : `Opens ${formatPlayTime(Date.parse(`${d.date}T00:00:00Z`) - 7 * 86_400_000)}`}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </main>
  );
}
